import { Plus, Pencil, Trash2, Check, X as XIcon } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AddAddressCard } from "./AddAddressCard";
import { useDispatch, useSelector } from "react-redux";
import {
  addAddress,
  deleteAddress,
  updateAddress,
} from "../../redux/slices/authSlice";
import { Address, User } from "../../types/slice.types";
import { RootState } from "../../redux/store";

export const Addresses = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector((state: RootState) => state.auth.user) as User;
  const addresses = user?.addresses || [];

  const [showForm, setShowForm] = useState(false);
  const [editAddress, setEditAddress] = useState<Address | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Address | null>(null);

  const deleteRef = useRef<HTMLDivElement | null>(null);

  const slots = Array.from({ length: 3 });

  const handleSubmit = (address: Address) => {
    if (editAddress) {
      dispatch(updateAddress(address) as any);
    } else {
      dispatch(addAddress(address) as any);
    }
    setShowForm(false);
    setEditAddress(null);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    dispatch(deleteAddress(deleteTarget._id as string) as any);
    setDeleteTarget(null);
  };

  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold mb-4">{t("Addresses")}</h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {slots.map((_, index) => {
          const address = addresses[index];

          if (address) {
            return (
              <div
                key={address._id}
                className="relative h-32 p-4 rounded-lg border bg-white dark:bg-gray-800 flex flex-col justify-between"
              >
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    onClick={() => {
                      setEditAddress(address);
                      setShowForm(true);
                    }}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                  >
                    <Pencil size={14} />
                  </button>

                  <button
                    onClick={() => setDeleteTarget(address)}
                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                  >
                    <Trash2 size={14} className="text-red-600" />
                  </button>
                </div>

                <div className="space-y-1 text-sm overflow-hidden">
                  <p className="font-medium truncate">{address.building}</p>
                  <p className="truncate text-xs">{address.street}</p>
                  <p className="truncate text-xs">
                    {address.city}, {address.country}
                  </p>
                </div>

                <p className="text-xs truncate">{address.phone}</p>
              </div>
            );
          }

          return (
            <div
              key={`add-${index}`}
              onClick={() => setShowForm(true)}
              className="h-32 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer"
            >
              <div className="flex flex-col items-center gap-2 opacity-70">
                <Plus size={28} />
                <span className="text-sm">{t("Add Address")}</span>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <AddAddressCard
          mode={editAddress ? "edit" : "add"}
          initialAddress={editAddress}
          onClose={() => {
            setShowForm(false);
            setEditAddress(null);
          }}
          onSubmit={handleSubmit}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div
            ref={deleteRef}
            className="bg-white dark:bg-gray-800 rounded-lg p-5 w-72"
          >
            <p className="text-sm mb-4">
              {t("Do you want to remove address?")}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="p-2 rounded-full hover:bg-gray-200"
              >
                <XIcon size={18} />
              </button>

              <button
                onClick={handleDelete}
                className="p-2 rounded-full hover:bg-red-100"
              >
                <Check size={18} className="text-red-600" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
